import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../Form/Button';
import { useLayoutContext } from '../../Contexts/LayoutContext';
import Img from './Img';
import { TbCircleCheckFilled } from 'react-icons/tb';

type Language = {
    code: string;
    name: string;
    label: string;
    flag: string;
};

interface BoxContentTranslateProps {
    className?: string;
    onBottom?: boolean;
    onRight?: boolean;
    onlyFlags?: boolean;
}

const BoxContentTranslate = ({
    className,
    onBottom = false,
    onRight = false,
    onlyFlags = false
}: BoxContentTranslateProps) => {

    const { t, i18n } = useTranslation();
    const { company } = useLayoutContext();

    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState<Language | null>(null);
    const dropdownRef = useRef<HTMLDivElement | null>(null);

    const languages = React.useMemo(
        () => company?.languages ?? [],
        [company?.languages]
    );
    const shouldRender = languages.length > 1;

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!languages.length) return;

        const baseLang = i18n.language.split('-')[0];

        const current = languages.find((lang: any) => {
            const langBase = lang.code?.split('-')[0];
            return langBase === baseLang;
        });

        setSelected(current || languages[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [languages, i18n.language]);

    useEffect(() => {
        if (selected && selected.code !== i18n.language) {
            i18n.changeLanguage(selected.code);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected, i18n]);

    if (!shouldRender) return null;

    const fromBottom = onBottom ? 'bottom-12' : 'top-12';
    const fromRight = onRight ? 'right-0' : 'left-0';

    return (
        <div className={`grid gap-1 items-center text-base leading-none ${onlyFlags ? '' : 'py-4'} ${className}`}>
            {!onlyFlags && (
                <h5 className="px-2 text-gray-400 text-sm">
                    {t('layout.language.title')}
                </h5>
            )}

            <div
                className={`relative inline-block text-left ${onlyFlags ? '' : 'mx-2'}`}
                ref={dropdownRef}
            >
                <Button
                    gray={onlyFlags && !open}
                    onClick={() => setOpen(prev => !prev)}
                    className={`flex items-center gap-2 ring-transparent rounded-md bg-white ${onlyFlags ? 'w-12' : ''}`}
                >
                    {selected ? (
                        <>
                            <Img src={selected.flag} alt={selected.name} className="max-w-6" />
                            {!onlyFlags && <span>{selected.label}</span>}
                        </>
                    ) : (
                        !onlyFlags && t('layout.language.title')
                    )}
                </Button>

                {open && (
                    <div
                        className={`absolute ${onlyFlags ? 'w-12' : 'w-40'} bg-white border border-gray-200 rounded-md shadow-lg z-20 ${fromBottom} ${fromRight}`}
                    >
                        <div className="flex grid place-items-center w-full divide-y divide-gray-50">
                            {languages.map((lang: any) => (
                                <button
                                    key={lang.code}
                                    disabled={selected?.code === lang.code}
                                    onClick={() => {
                                        setSelected(lang);
                                        setOpen(false);
                                    }}
                                    className={`relative flex items-center w-full ${onlyFlags ? 'py-3' : 'p-3'} disabled:pointer-events-none disabled:bg-gray-50 justify-center gap-2 hover:bg-gray-100 text-left ${
                                        selected?.code === lang.code
                                            ? 'font-semibold text-shopfood-title'
                                            : ''
                                    }`}
                                >
                                    <Img src={lang.flag} alt="" className="max-w-4" />
                                    {!onlyFlags && <span className="w-full">{lang.label}</span>}
                                    {selected?.code === lang.code && (
                                        <div className={onlyFlags ? 'absolute top-0 right-0' : ''}>
                                            <TbCircleCheckFilled size={15} className="text-custom-primary" />
                                        </div>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BoxContentTranslate;
